import React, { useState, useEffect, useMemo, useRef } from 'react';
import { leadsAPI, rdvAPI } from '../services/api';
import CalendarPopup from './CalendarPopup';

// ─────────────────────────────────────────────────────────────────────────────
// Script d'appel INTERACTIF — cold calling assurance, Canada B2C, prise de RDV.
//
// Le script complet est TOUJOURS VISIBLE, de haut en bas (aucune étape cachée) :
// l'agent lit le déroulé dans l'ordre pendant l'appel et note les réponses du
// client au fur et à mesure. 65 ans et plus → conclusion polie (hors cible) ; le
// statut propriétaire/locataire ne disqualifie pas, il sert à savoir si
// l'assurance hypothécaire s'applique. À la fin, le résumé complet est généré
// pour copier/coller dans le spreadsheet ET enregistré sur la fiche du client
// (custom_fields).
//
// Les clients (noms + liens calendrier) viennent de
// advisorsAPI — éditables dans le CRM Admin, jamais codés en dur ici.
// ─────────────────────────────────────────────────────────────────────────────

const ASSURANCES = ['Vie', 'Invalidité', 'Hypothécaire', 'Maladie'];
const FOURNISSEURS = ['Banque', 'Privé (IA)', 'Privé (autre)'];

const OBJECTIONS = [
  {
    q: "Ça ne m'intéresse pas / Je suis trop occupé.",
    a: "Je comprends que vous soyez occupé, c'est pourquoi je ne vous prends que quelques secondes. Le but est de confirmer si un de nos experts peut vous faire économiser de l'argent. Si ce n'est pas le cas, vous n'y perdez rien. Je vous propose de fixer rapidement 15 minutes pour vérifier cela, quand seriez-vous disponible la semaine prochaine ?",
  },
  {
    q: "Je suis satisfait de mon assurance actuelle / J'ai déjà un courtier.",
    a: "C'est une excellente nouvelle, cela veut dire que vous êtes bien assuré. Nous ne cherchons pas à remplacer votre courtier, mais à vous offrir un service de comparaison. Nos experts ont accès à un éventail de produits plus large. Même si vous êtes satisfait, vérifier si vous payez le meilleur prix est toujours une bonne pratique. Je vous propose la semaine prochaine pour une vérification rapide, quel jour vous arrange ?",
  },
  {
    q: "Comment avez-vous obtenu mes informations ?",
    a: "Étant donné que nous faisons du démarchage à froid : si vous êtes client chez IA, je vous appelle pour une mise à jour de votre dossier. Si non-client, votre nom et numéro proviennent de l'annuaire public, car nous faisons une campagne partout au Québec. Je vous assure que toutes vos données sont protégées. Voulez-vous que je retire votre numéro de nos listes ou préférez-vous poursuivre la discussion ?",
  },
  {
    q: "Je n'ai pas le temps / Pas intéressé.",
    a: "Je comprends, ça ne prend que 2 minutes pour noter vos critères. Le conseiller fait tout le travail de comparaison pour vous et vous envoie les 3 meilleures offres par email. Vous n'avez qu'à choisir. Ça vous va si on note juste le type d'assurance et votre code postal ?",
  },
];

function initialAnswers(lead) {
  return {
    statut_residentiel: null,
    // Karim 2026-10-20 : « default age /65 ans — if manual adding then use age
    // added » — PAS d'âge saisi = -65 ans (la cible du script), précoché dès
    // l'ouverture. L'agent qui tape un âge manuel remplace la valeur par défaut
    // (onChange remet age_refuse à false).
    age: '',
    age_refuse: true,
    assurances: [],
    fournisseurs: {},
    autre_precision: '',
    mode_rdv: null,
    courriel: lead?.email || '',
    ligne2: '',
    prenom_client: lead?.first_name || '',
    date_rdv: '',
    heure_rdv: '',
    conseiller_id: null,
    commentaires: '',
  };
}

export default function CallFlow({ campaign, lead, user, advisors, width = 280, onRdvBooked }) {
  const [tab, setTab] = useState('flow');       // flow | talk | objections | summary
  const [a, setA] = useState(() => initialAnswers(lead));
  const [outcome, setOutcome] = useState(null); // null | 'rdv' | 'hors_cible' | 'pas_interesse'
  const [saveState, setSaveState] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [copied, setCopied] = useState(false);
  const [calCopied, setCalCopied] = useState(false); // copie du commentaire calendrier
  const [dncl, setDncl] = useState(null);       // null (inconnu) | 'ok' | 'blocked' — DNCL auto-check
  const [advRdvs, setAdvRdvs] = useState(null); // créneaux déjà réservés du client sélectionné
  const [showSlots, setShowSlots] = useState(false);
  const [dnclPopup, setDnclPopup] = useState(null); // null | {checking, result} — vérif manuelle (popup, fallback)
  const [calendarAdvisor, setCalendarAdvisor] = useState(null); // conseiller dont le calendrier popup est ouvert (nom caché dans le popup)
  // Confirmation OBLIGATOIRE du statut DNCL avant de pouvoir réserver (Karim,
  // 2026-10-12) : 'auto-ok' (cochée automatiquement quand l'auto-check est OK),
  // 'manual-ok' (l'agent a vérifié lui-même), 'listed' (bloque), null (à faire).
  const [dnclManual, setDnclManual] = useState(null);
  // RDV réservé par ce script (id) — alimente le bouton « Annuler » (undo) :
  // l'annulation appelle directement POST /rdv/:id/cancel-error.
  const [rdvId, setRdvId] = useState(null);
  // ── Brouillon local (Karim, 2026-10-16 : « why data are lost!! ») ────────────
  // Incident : la sauvegarde du script échouait en silence et les réponses
  // n'existaient plus nulle part. Chaque réponse est maintenant persistée dans
  // localStorage (clé par lead) — reload/crash/flap = tout est restauré.
  const [draftRestored, setDraftRestored] = useState(false);
  const [saveError, setSaveError]   = useState(null); // message serveur à afficher
  const draftKey = lead?.id ? `rdv_draft_${lead.id}` : null;
  const draftOwnerRef = useRef(null); // clé dont l'état courant est propriétaire

  const set = (patch) => setA(prev => ({ ...prev, ...patch }));

  // ── Réponses = champs de la fiche client (Karim 2026-10-20 : « replace lead
  // record fields — simpler and robust, no duplicate/empty fields ») ──────────
  // Une seule source de vérité : les réponses vivent À PLAT dans
  // custom_fields (+ email/prénom dans les vraies colonnes leads). Les copies
  // script_flow/script_summary sont supprimées — le résumé du RDV vit dans
  // rendezvous.comments (ce que les conseillers lisent).
  const flatAnswers = (ans, extra = {}) => ({
    statut_residentiel: ans.statut_residentiel || '',
    age: ans.age_refuse ? '-65 ans' : (ans.age || ''),
    age_refuse: !!ans.age_refuse,
    assurances: ans.assurances,
    fournisseurs: ans.fournisseurs,
    autre_precision: ans.autre_precision || '',
    mode_rdv: ans.mode_rdv || '',
    courriel: ans.courriel || '',
    ligne2: ans.ligne2 || '',
    prenom_client: ans.prenom_client || '',
    date_rdv: ans.date_rdv || '',
    heure_rdv: ans.heure_rdv || '',
    conseiller_id: ans.conseiller_id,
    commentaires: ans.commentaires || '',
    ...extra,
  });

  // AUTO-SAUVEGARDE SERVEUR (Karim 2026-10-20) : chaque réponse est persistée
  // sur la fiche ~1s après sa saisie — les données existent même si l'agent ne
  // clique jamais « Confirmer » (crash, onglet fermé, appel perdu). Le
  // brouillon localStorage reste pour le restore instantané ; ceci est la
  // vérité durable. Silencieux : finish()/book reste la validation bloquante.
  useEffect(() => {
    if (!draftKey || draftOwnerRef.current !== draftKey) return;
    if (saveState === 'saved') return; // fiche enregistrée — rien à pousser
    const t = setTimeout(() => {
      let cf = {};
      try {
        cf = typeof lead?.custom_fields === 'string'
          ? JSON.parse(lead.custom_fields) : (lead?.custom_fields || {});
      } catch (_) { cf = {}; }
      delete cf.script_flow; delete cf.script_summary; // doublons remplacés
      const merged = { ...cf, ...flatAnswers(a, { dncl_auto: dncl, dncl_confirmed: dnclManual }) };
      const body = { custom_fields: merged };
      const em = (a.courriel || '').trim();       if (em) body.email = em;
      const pn = (a.prenom_client || '').trim();  if (pn && pn !== lead?.first_name) body.first_name = pn;
      leadsAPI.update(lead.id, body).catch(() => { /* best effort — le finish reste bloquant */ });
    }, 1000);
    return () => clearTimeout(t);
  }, [draftKey, a, dncl, dnclManual, saveState]);

  // Persistance du brouillon — APRÈS chaque changement (l'ordre de déclaration
  // compte : cet effet doit s'exécuter AVANT le reset ci-dessous, sinon un
  // changement de lead écraserait le brouillon du NOUVEAU client avec les
  // réponses de l'ANCIEN).
  useEffect(() => {
    if (!draftKey || draftOwnerRef.current !== draftKey) return;
    if (saveState === 'saved') return; // enregistré → brouillon déjà effacé
    try {
      localStorage.setItem(draftKey, JSON.stringify({ a, outcome, dncl: dnclManual, rdv_id: rdvId, ts: Date.now() }));
    } catch (_) { /* quota/privé — best effort */ }
  }, [draftKey, a, outcome, dnclManual, saveState]);

  // Nouvel appel = nouveau client → réinitialiser le script… SAUF si un
  // brouillon existe (crash/reload au milieu du script) : tout est restauré.
  useEffect(() => {
    if (!draftKey) {
      // Plus de lead (entre deux appels) : retour à un script vierge, comme
      // avant l'ajout des brouillons.
      draftOwnerRef.current = null;
      setTab('flow'); setOutcome(null);
      setSaveState(null); setCopied(false); setRdvId(null);
      setDncl(null); setDnclManual(null); setShowSlots(false); setAdvRdvs(null);
      setDnclPopup(null);
      setDraftRestored(false); setSaveError(null);
      setA(initialAnswers(null));
      return;
    }
    draftOwnerRef.current = null;
    setTab('flow'); setOutcome(null);
    setSaveState(null); setCopied(false); setRdvId(null);
    setDncl(null); setDnclManual(null); setShowSlots(false); setAdvRdvs(null);
    setDnclPopup(null);
    setDraftRestored(false); setSaveError(null);
    let restored = false;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        // Brouillon de moins de 24 h seulement — un lead rappelé des semaines
        // plus tard ne doit pas hériter de vieilles réponses.
        if (d && d.a && Date.now() - (d.ts || 0) < 24 * 3600 * 1000) {
          setA(d.a);
          setOutcome(d.outcome || null);
          setRdvId(d.rdv_id || null);
          setDnclManual(d.dncl || null);
          if (d.outcome) {
            setTab('summary');
            // Un brouillon qui survit = la sauvegarde n'a JAMAIS abouti (le
            // brouillon est effacé dans la même milliseconde que le succès).
            // Afficher l'état NON ENREGISTRÉ + Réessayer — pas un faux « OK ».
            setSaveState('error');
            setSaveError(null);
          }
          restored = true;
          setDraftRestored(true);
          setTimeout(() => setDraftRestored(false), 8000);
        }
      }
    } catch (_) { /* brouillon corrompu → repartir à zéro */ }
    if (!restored) setA(initialAnswers(lead));
    draftOwnerRef.current = draftKey;
  }, [draftKey]);

  // DNCL — validation UNIQUEMENT à la prise de rendez-vous (Karim) : jamais au
  // début de l'appel (les tentatives multiples restent libres) ; le contrôle
  // part tout seul dès que l'agent fixe la date ET l'heure du RDV (étape 9).
  useEffect(() => {
    if (!a.date_rdv || !a.heure_rdv || !lead?.phone) { setDncl(null); setDnclManual(null); return; }
    let live = true;
    setDncl('checking');
    setDnclManual(null);       // re-check = la confirmation repart de zéro
    rdvAPI.dnclCheck(lead.phone)
      .then(r => { if (live) { setDncl(r.listed ? 'blocked' : 'ok'); setDnclManual(r.listed ? 'listed' : 'auto-ok'); } })
      .catch(() => { if (live) setDncl(null); });
    return () => { live = false; };
  }, [a.date_rdv, a.heure_rdv, lead && lead.id, lead && lead.phone]);

  // Client sélectionné → charger ses rendez-vous déjà pris (créneaux occupés),
  // pour que l'agent propose des heures libres sans quitter le CRM.
  useEffect(() => {
    if (!a.conseiller_id) { setAdvRdvs(null); return; }
    rdvAPI.list({ advisor_id: a.conseiller_id })
      .then(d => setAdvRdvs((Array.isArray(d) ? d : []).filter(r => r.statut !== 'annule')))
      .catch(() => setAdvRdvs([]));
  }, [a.conseiller_id]);

  const advisor = useMemo(
    () => advisors.find(x => x.id === Number(a.conseiller_id)) || null,
    [advisors, a.conseiller_id]
  );

  // Pure builder — used by the memo AND by finish(), which needs the summary
  // for the *new* outcome before the next render recomputes the memo.
  const buildSummary = (oc, ans, adv) => {
    if (!oc) return '';
    const today = new Date().toLocaleDateString('fr-CA');
    const statut = oc === 'rdv' ? 'RDV confirmé'
                 : oc === 'hors_cible' ? 'Hors cible' : 'Pas intéressé';
    const four = Object.entries(ans.fournisseurs).map(([k, v]) => `${k}: ${v}`).join('; ');
    const mode = ans.mode_rdv === 'virtuel' ? 'Virtuel (Zoom/Teams)' : 'Téléphonique';
    return [
      `Date : ${today}`,
      `Agent : ${user?.username || '?'}`,
      `Nom et Prénom : ${lead?.last_name || '?'}, ${ans.prenom_client || lead?.first_name || '?'}`,
      `Téléphone : ${lead?.phone || '?'}`,
      `Courriel : ${ans.courriel || lead?.email || ''}`,
      `Date et heure RDV : ${oc === 'rdv' ? `${ans.date_rdv} à ${ans.heure_rdv}` : ''}`,
      `Conseiller : ${adv?.name || ''}`,
      `DNCL : ${ans.dncl_auto === 'blocked' || ans.dncl_confirmed === 'listed' ? 'numéro LISTÉ'
        : ans.dncl_auto === 'ok' ? 'non listé (vérifié auto)'
        : ans.dncl_confirmed === 'manual-ok' ? 'non listé (confirmé agent)' : ''}`,
      `Commentaires : ${ans.commentaires}`,
      `Statut: ${statut}`,
      `Statut résidentiel: ${ans.statut_residentiel || ''}`,
      `Âge: ${ans.age_refuse ? '-65 ans' : ans.age}`,
      `Assurances: ${ans.assurances.join(', ')}`,
      `Fournisseur: ${four}`,
      `Mode RDV: ${oc === 'rdv' ? mode : ''}`,
      `Ligne 2: ${ans.ligne2}`,
      `Campagne: ${campaign?.name || ''}`,
    ].join(' | ');
  };

  const summaryText = useMemo(
    () => buildSummary(outcome, { ...a, dncl_auto: dncl, dncl_confirmed: dnclManual }, advisor),
    [outcome, a, advisor, lead, user, campaign, dncl, dnclManual]
  );

  // Commentaire RDV pour le calendrier (Karim 2026-10-20) : même texte que le
  // résumé final, construit EN DIRECT dès la saisie (outcome forcé à 'rdv') —
  // affiché AVANT la sélection du client pour copier-coller dans son calendrier.
  const calendarText = useMemo(
    () => buildSummary('rdv', { ...a, dncl_auto: dncl, dncl_confirmed: dnclManual }, advisor),
    [a, advisor, lead, user, campaign, dncl, dnclManual]
  );

  // Fin d'appel : enregistre le flux + le résumé sur la fiche du client.
  // 'rdv' → réservation ATOMIQUE (POST /rdv/book, 2026-10-16) : le serveur
  // valide les champs obligatoires (date, heure, client, type, DNCL) et écrit
  // RDV + fiche dans UNE transaction — plus jamais de « RDV confirmé » sans
  // ligne rendezvous en base. Les autres conclusions passent toujours par le
  // PUT /leads (comportement inchangé).
  const clearDraft = () => { if (draftKey) { try { localStorage.removeItem(draftKey); } catch (_) {} } };

  const finish = async (oc) => {
    setOutcome(oc);
    setTab('summary');
    if (!lead?.id) return;
    setSaveState('saving');
    setSaveError(null);
    try {
      let custom = {};
      try {
        custom = typeof lead.custom_fields === 'string'
          ? JSON.parse(lead.custom_fields) : (lead.custom_fields || {});
      } catch (_) {}
      // Résumé complet → commentaires du RDV (Karim 2026-10-15) : le backend
      // colle flow.commentaires dans rendezvous.comments — c'est ce que les
      // conseillers lisent. La note libre de l'agent reste incluse (ligne
      // « Commentaires : » du résumé), donc rien n'est perdu.
      // (2026-10-20, single source : le résumé ne vit PLUS sur la fiche —
      // réponses à plat dans custom_fields, résumé dans rendezvous.comments.)
      const flowWithDncl = { ...a, dncl_auto: dncl, dncl_confirmed: dnclManual };
      const summary = buildSummary(oc, flowWithDncl, advisors.find(x => x.id === Number(a.conseiller_id)) || null);
      const merged = {
        ...custom,
        ...flatAnswers(a, { dncl_auto: dncl, dncl_confirmed: dnclManual, outcome: oc, commentaires: summary }),
      };
      delete merged.script_flow; delete merged.script_summary; // doublons remplacés (Karim 2026-10-20)
      const newEmail = (a.courriel || '').trim();
      const newPrenom = (a.prenom_client || '').trim();
      if (oc === 'rdv') {
        // Réservation atomique : tout ou rien, idempotente (le « Réessayer » ne
        // crée jamais de doublon). Le serveur re-valide les 5 champs obligatoires.
        const r = await rdvAPI.book({
          lead_id: lead.id,
          custom_fields: merged,
          summary,
          email: newEmail || undefined,
          first_name: newPrenom || undefined,
        });
        setSaveState('saved');
        setRdvId(r?.rdv_id || null);
        clearDraft();
        if (onRdvBooked) onRdvBooked(r?.rdv_id);
      } else {
        const leadUpdate = { custom_fields: merged };
        if (newEmail) leadUpdate.email = newEmail;
        if (newPrenom && newPrenom !== lead?.first_name) leadUpdate.first_name = newPrenom;
        await leadsAPI.update(lead.id, leadUpdate);
        setSaveState('saved');
        clearDraft();
      }
    } catch (e) {
      // BLOQUANT (Karim, 2026-10-16) : l'échec d'enregistrement n'est plus
      // silencieux. Le RDV n'existe pas en base → le barrage APPT côté serveur
      // refusera la disposition APPT de toute façon. L'agent voit l'erreur du
      // serveur, peut réessayer (idempotent) ou reprendre le script — et peut
      // toujours choisir un autre résultat avec les détails en commentaire.
      setSaveState('error');
      setSaveError(e?.error || e?.message || 'Erreur d\'enregistrement');
    }
  };

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = summaryText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCalendarText = async () => {
    try {
      await navigator.clipboard.writeText(calendarText);
    } catch (_) {
      const ta = document.createElement('textarea');
      ta.value = calendarText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCalCopied(true);
    setTimeout(() => setCalCopied(false), 2000);
  };

  // Validation par erreur : annule la confirmation enregistrée sur la fiche
  // (outcome → 'annule', résumé vidé) puis rouvre le script — les réponses
  // déjà saisies sont conservées, l'agent corrige et reconfirme.
  const undoConfirm = async () => {
    if (!window.confirm('Annuler cette validation ? Le client reprendra sa place dans le script et vous pourrez corriger les données.')) return;
    // Single source (2026-10-20) : l'annulation passe par le ENDPOINT dédié du
    // RDV (statut 'annule' + note d'audit) — plus de copie script_flow. Les
    // réponses restent telles quelles sur la fiche (champs plats), l'agent
    // corrige puis re-confirme.
    if (rdvId) {
      try { await rdvAPI.cancelError(rdvId); } catch (_) { /* RDV reste actif — l'agent peut l'annuler via la grille */ }
    }
    setRdvId(null);
    setOutcome(null); setSaveState(null); setCopied(false);
    setTab('flow');
  };

  const ageNum = parseInt(a.age, 10);
  const ageHorsCible = Number.isFinite(ageNum) && ageNum >= 65;
  const rdvOk = Boolean(a.date_rdv && a.heure_rdv);
  // DNCL confirmé (auto ou manuel) = seule voie vers "RDV confirmé".
  const dnclManualOk = dnclManual === 'auto-ok' || dnclManual === 'manual-ok'; // true = non listé confirmé
  const canBookRdv = rdvOk && dnclManualOk;

  // ── morceaux réutilisables ─────────────────────────────────────────────────
  // (Karim, 2026-10-15 : moins de couleurs, pas de boîtes pour les réponses —
  //  texte brut + points sélectionnables, lisible en pleine conversation.)

  // Texte à lire au client : texte brut, PAS de boîte/fond — juste un peu plus
  // grand pour se distinguer des consignes.
  const pitchBox = (text) => (
    <div style={{
      fontSize: 14.5, lineHeight: 1.75, fontWeight: 700, color: 'var(--text)', marginBottom: 6,
    }}>
      <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text2)', marginRight: 6 }}>À lire</span>
      {text}
    </div>
  );

  // Bouton d'action (conclure, RDV confirmé…) : neutre — bordure fine, fond
  // transparent, texte normal. Aucune couleur décorative.
  const bigBtn = (label, onClick, opts = {}) => (
    <button key={label} className="btn" onClick={onClick}
      style={{
        flex: 1, padding: '10px 12px', fontSize: 13, fontWeight: 700,
        whiteSpace: 'normal', lineHeight: 1.3,
        background: 'transparent', border: '1px solid var(--border)',
        color: 'var(--text)', ...opts.style,
      }}>
      {label}
    </button>
  );

  // Réponse à une question : POINT SÉLECTIONNABLE (pas de boîte). Une ligne,
  // un point qui se remplit, le texte passe en gras. single = radio,
  // multi = case cochable.
  const pickPoint = (label, selected, onClick, multi = false) => (
    <button type="button" key={label} onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: '9px 2px', background: 'none', border: 'none', cursor: 'pointer',
        textAlign: 'left', fontSize: 13.5, lineHeight: 1.35,
        color: selected ? 'var(--text)' : 'var(--text2)',
        fontWeight: selected ? 800 : 500,
        borderBottom: '1px solid var(--border)',
      }}>
      <span style={{
        width: 15, height: 15, flexShrink: 0,
        borderRadius: multi ? 4 : '50%',
        border: `2px solid ${selected ? 'var(--text)' : 'var(--border)'}`,
        background: selected ? 'var(--text)' : 'transparent',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--bg)', fontSize: 11, fontWeight: 900, lineHeight: 1,
      }}>
        {multi && selected ? '✓' : ''}
      </span>
      {label}
    </button>
  );

  // Consigne pour l'agent : texte secondaire sobre, plus d'orange.
  const hint = (text) => (
    <div style={{ fontSize: 11.5, color: 'var(--text2)', marginBottom: 12, lineHeight: 1.5 }}>{text}</div>
  );

  const clientCard = lead && (
    <div style={{
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 10px',
      padding: '8px 10px', background: 'rgba(255,255,255,0.03)',
      border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12, fontSize: 12,
    }}>
      <div style={{ gridColumn: '1 / -1', fontWeight: 800, fontSize: 13 }}>
        {[lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Client'}
      </div>
      <div>Tél : {lead.phone || '—'}</div>
      <div>Ville : {lead.city || '—'}</div>
      <div style={{ gridColumn: '1 / -1' }}>Courriel : {lead.email || a.courriel || '—'}</div>
    </div>
  );

  const stepTitle = (n, t) => (
    <div style={{
      fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
      letterSpacing: '0.05em', color: 'var(--text2)',
      borderTop: '1px solid var(--border)', paddingTop: 12, margin: '16px 0 8px',
    }}>
      Étape {n} — {t}
    </div>
  );

  // ── le script complet, toujours visible de haut en bas ─────────────────────
  const renderFlow = () => (
    <>
      {clientCard}

      {/* Brouillon restauré (crash/reload au milieu du script) — Karim 2026-10-16 */}
      {draftRestored && (
        <div style={{
          padding: '8px 10px', marginBottom: 10, borderRadius: 8, fontSize: 11.5, fontWeight: 700,
          background: 'rgba(16,185,129,0.12)', border: '1px solid var(--green)', color: 'var(--green)',
        }}>
          ✓ Brouillon restauré — vos réponses précédentes ont été conservées.
        </div>
      )}

      {outcome === 'hors_cible' && (
        <>
          {pitchBox("Merci pour votre temps, bonne journée !")}
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
            Hors cible — 65 ans et plus. Concluez poliment.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => { setOutcome(null); setA(initialAnswers(lead)); }}>↺ Nouveau client</button>
          </div>
        </>
      )}
      {outcome === 'pas_interesse' && (
        <>
          {pitchBox("Je vous remercie de votre temps. Si jamais vous changez d'idée, n'hésitez pas à nous contacter. Bonne journée !")}
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
            Le client n'est pas intéressé. Raccrochez poliment puis choisissez la disposition.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => { setOutcome(null); setA(initialAnswers(lead)); }}>↺ Nouveau client</button>
          </div>
        </>
      )}

      {!outcome && (
        <>
          {stepTitle(1, 'Introduction')}
          {pitchBox(<>Bonjour <strong>{[lead?.first_name, lead?.last_name].filter(Boolean).join(' ') || 'Monsieur/Madame'}</strong>. Je suis <strong>{user?.full_name || user?.username}</strong>, d'Industrielle Alliance. Ça va bien ? <span style={{ color: 'var(--text2)', fontStyle: 'italic' }}>(ne pas dire « vous allez bien ? »)</span> Super ! Nous avons actuellement une campagne d'information partout au Québec concernant les assurances. Je vous explique rapidement ? Merci !</>)}
          {hint("Rassurez le client par votre voix et votre écoute. Créez un climat de confiance dès les premières secondes.")}
          {bigBtn('Pas intéressé — conclure', () => finish('pas_interesse'))}

          <div style={{ marginTop: 18 }} />  {/* Aération */}

          {stepTitle(2, 'Statut de résidence')}
          {pitchBox("Si je peux me permettre, vous êtes propriétaire ou locataire ?")}
          {hint("CIBLE : moins de 65 ans — propriétaire ou locataire. Le statut sert à savoir si l'assurance Hypothécaire s'applique.")}
          <div style={{ marginBottom: 12 }}>
            {pickPoint('Propriétaire', a.statut_residentiel === 'Propriétaire', () => set({ statut_residentiel: 'Propriétaire' }))}
            {pickPoint('Locataire', a.statut_residentiel === 'Locataire', () => set({ statut_residentiel: 'Locataire' }))}
          </div>

          <div style={{ marginTop: 18 }} />  {/* Aération */}

          {stepTitle(3, 'Âge')}
          {pitchBox("ET vous avez quel âge ? / Vous avez moins de 65 ans ?")}
          <input
            type="number" min="18" max="110" inputMode="numeric"
            className="inp" placeholder="Âge (ex : 45)"
            value={a.age}
            onChange={e => set({ age: e.target.value, age_refuse: false })}
            style={{ width: '100%', padding: '10px 12px', fontSize: 14, marginBottom: 10 }}
          />
          <button className="btn btn-ghost" style={{ width: '100%', fontSize: 12, marginBottom: 8 }}
            onClick={() => set({ age_refuse: true, age: '' })}>
            -65 ans
          </button>
          {ageHorsCible && (
            <>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--red)', margin: '4px 0 8px' }}>
                65 ans et plus → hors cible. Concluez poliment.
              </div>
              {bigBtn('Conclure hors cible', () => finish('hors_cible'))}
            </>
          )}

          <div style={{ marginTop: 18 }} />  {/* Aération */}

          {stepTitle(4, 'Types d\'assurances')}
          {pitchBox(<>Parfait. Je vous appelle puisqu'il y a des changements au niveau des contrats afin de s'adapter à la crise et aux conditions économiques ! Et vous offrir une consultation gratuite pour vérifier s'il y a des avantages qui peuvent vous concerner. L'objectif est de bénéficier d'un contrat plus avantageux sur vos assurances.</>)}
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            Vous avez quels types d'assurances actuellement ?
          </div>
          {hint(a.statut_residentiel === 'Propriétaire'
            ? 'Propriétaire — vérifiez si l\'assurance Hypothécaire est incluse.'
            : 'Locataire — l\'assurance Hypothécaire ne s\'applique généralement pas.')}
          <div style={{ marginBottom: 12 }}>
            {ASSURANCES.map(ins => {
              const on = a.assurances.includes(ins);
              return pickPoint(ins, on, () => set({
                assurances: on ? a.assurances.filter(x => x !== ins) : [...a.assurances, ins],
              }), true);
            })}
          </div>

          <div style={{ marginTop: 18 }} />  {/* Aération */}

          {stepTitle(5, 'Fournisseur')}
          {pitchBox("L'assurance (ou les assurances) mentionnée(s) est-elle(s) avec la Banque, ou une assurance Privée (Industrielle Alliance ou autre) ?")}
          {a.assurances.map(ins => (
            <div key={ins} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>{ins}</div>
              <select
                className="sel" value={a.fournisseurs[ins] || ''}
                onChange={e => set({ fournisseurs: { ...a.fournisseurs, [ins]: e.target.value } })}
                style={{ width: '100%', padding: '8px 10px', fontSize: 13 }}
              >
                <option value="">— Choisir —</option>
                {FOURNISSEURS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          ))}
          <input
            className="inp" placeholder="Autre (spécifiez)…"
            value={a.autre_precision}
            onChange={e => set({ autre_precision: e.target.value })}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 12 }}
          />

          {stepTitle(6, 'Client — disponibilités')}
          {pitchBox(<>Parfait ! Je peux vous proposer une consultation gratuite avec l'un de nos conseillers. Laissez-moi vérifier ses disponibilités pour vous suggérer des plages horaires…</>)}

          {/* Commentaire RDV → calendrier du client (Karim 2026-10-20) : le
              texte se remplit EN DIRECT (nom, téléphone, date/heure, mode,
              conseiller dès qu'ils sont saisis) AVANT la sélection du client —
              l'agent copie puis colle dans le calendrier ouvert via le lien.
              Karim 2026-10-23 : affiché IMMÉDIATEMENT avec toutes les données
              déjà saisies — n'attend plus la date/heure. */}
          <div style={{ margin: '14px 0', padding: '12px 14px', borderRadius: 8, background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)' }}>
                📝 Commentaire RDV — à copier-coller dans le calendrier du client
              </span>
              <button className="btn btn-ghost" style={{ fontSize: 12, whiteSpace: 'nowrap' }} onClick={copyCalendarText}>
                {calCopied ? '✓ Copié' : '📋 Copier'}
              </button>
            </div>
            <div style={{ fontSize: 11.5, lineHeight: 1.7, wordBreak: 'break-word' }}>
              {calendarText}
            </div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>
            Client (conseiller) — sélectionnez pour voir ses créneaux :
          </div>
          {/* Équilibrage (Karim, 2026-10-12) : chaque client actif affiche son
              nombre de RDV à venir ; le/les moins chargés sont mis en avant
              pour que tous les clients gardent un volume similaire. */}
          {(() => {
            const minRdv = Math.min(...(advisors.length ? advisors.map(x => x.rdv_count || 0) : [0]));
            return advisors.some(x => (x.rdv_count || 0) === minRdv && advisors.length > 1) ? (
              <div style={{ fontSize: 11.5, color: 'var(--text2)', marginBottom: 6, lineHeight: 1.5 }}>
                Équilibrage : privilégiez le client avec le moins de RDV à venir (★) — tous les clients actifs doivent rester à un volume similaire.
              </div>
            ) : null;
          })()}
          {/* Conseillers actifs — cliquer un prénom ouvre son calendrier.
              Le popup affiche uniquement le calendrier, nom caché (Karim 2026-10-29).
              Pas de sélection : le prénom est un lien permanent, pas un choix à valider. */}
          <div style={{ marginBottom: 10 }}>
            {advisors.map(adv => {
              const n = adv.rdv_count || 0;
              const minRdv = Math.min(...(advisors.length ? advisors.map(x => x.rdv_count || 0) : [0]));
              const firstName = (adv.name || '').split(' ')[0];
              return (
                <div key={adv.id}
                  onClick={() => setCalendarAdvisor(adv)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '9px 12px', borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: '#fff', cursor: 'pointer', marginBottom: 6, fontSize: 13, color: 'var(--text)',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.06)'; e.currentTarget.style.borderColor = 'var(--purple)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  <div style={{ fontWeight: 700 }}>{firstName}</div>
                  <div style={{ fontSize: 11, color: 'var(--text2)' }}>{n} RDV à venir{n === minRdv && advisors.length > 1 ? ' — le moins chargé' : ''}</div>
                  <span style={{ fontSize: 11, color: 'var(--purple)', fontWeight: 800 }}>📅 Ouvrir le calendrier</span>
                  {adv.calendar_url || adv.spreadsheet_url ? (
                    <span style={{ fontSize: 10, color: '#059669', fontWeight: 700, marginLeft: 8 }}>✓ Calendrier configuré</span>
                  ) : (
                    <span style={{ fontSize: 10, color: '#ea580c', fontWeight: 700, marginLeft: 8 }}>⚠️ Aucun calendrier</span>
                  )}
                </div>
              );
            })}
            {advisors.length === 0 && <div style={{ fontSize: 12, color: 'var(--text2)', padding: '8px 0' }}>Aucun conseiller actif.</div>}
          </div>
          {advisor && advisor.calendar_url && (
            <button
              type="button" className="btn btn-ghost btn-sm"
              onClick={() => setShowSlots(s => !s)}
              style={{ marginBottom: 8 }}
              title="Voir les rendez-vous déjà réservés pour ce conseiller"
            >{showSlots ? 'Masquer les créneaux pris' : 'Créneaux déjà réservés'}</button>
          )}
          {advisor && (
            <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', marginBottom: 8, lineHeight: 1.5 }}>
              🕐 Heure du Québec (Montréal) — vérifiez toujours le fuseau affiché dans le calendrier avant de proposer une plage.
            </div>
          )}
          {advisor && showSlots && (
            <div style={{
              background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              padding: 10, marginBottom: 12, maxHeight: 180, overflowY: 'auto',
            }}>
              {advRdvs === null ? <div style={{ fontSize: 12, color: 'var(--text2)' }}>Chargement…</div>
              : advRdvs.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text2)' }}>Aucun rendez-vous réservé — toutes les plages sont libres.</div>
              : advRdvs.map(r => (
                <div key={r.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>{String(r.rdv_date).slice(0, 10)} à {String(r.rdv_time || '').slice(0, 5)}</span>
                  <span style={{ color: 'var(--text2)' }}>
                    {r.mode === 'virtuel' ? 'virtuel' : r.mode === 'telephonique' ? 'téléphone' : ''} · {r.client_first || ''} {r.client_last || ''}
                  </span>
                </div>
              ))}
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6 }}>
                Ne proposez pas ces plages au prospect — vérifiez aussi le calendrier pour les heures de travail.
              </div>
            </div>
          )}

          <div style={{ marginTop: 18 }} />  {/* Aération */}

          {stepTitle(7, 'Mode de consultation')}
          {pitchBox(<>Parfait c'est noté ! Donc en général vous êtes disponible les matins ou les après-midis / vous êtes disponible demain ou après-demain ?</>)}
          {hint("Notre conseiller peut proposer une rencontre virtuelle via Zoom/Teams ou un contact téléphonique, selon ce qui convient le mieux. Mode de consultation préféré :")}
          <div style={{ marginBottom: 12 }}>
            {pickPoint('Virtuel (Zoom / Teams)', a.mode_rdv === 'virtuel', () => set({ mode_rdv: 'virtuel' }))}
            {pickPoint('Téléphonique', a.mode_rdv === 'telephonique', () => set({ mode_rdv: 'telephonique' }))}
          </div>

          <div style={{ marginTop: 18 }} />  {/* Aération */}

          {stepTitle(8, 'Coordonnées')}
          {/* Prénom (Karim 2026-10-15) : la liste 1001 n'a pas de prénoms —
              capturé pendant l'appel, il alimente la fiche RDV (nom et prénom
              du prospect) et le résumé. */}
          {pitchBox(<>Au fait, quel est votre prénom ?</>)}
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>Prénom du prospect (requis pour la fiche)</div>
          <input
            className="inp"
            value={a.prenom_client}
            onChange={e => set({ prenom_client: e.target.value })}
            placeholder="ex : Marie"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 10 }}
          />
          {a.mode_rdv === 'virtuel' && pitchBox(<>D'accord, vous préférez une rencontre virtuelle via Zoom/Teams ! Pouvez-vous me confirmer votre courriel pour l'invitation ?</>)}
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>Courriel à confirmer</div>
          <input
            className="inp" type="email"
            value={a.courriel}
            onChange={e => set({ courriel: e.target.value })}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 10 }}
          />
          {pitchBox(<>On peut utiliser ce numéro ({lead?.phone || '…'}) pour vous joindre ?</>)}
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>
            Numéro additionnel (Ligne 2) — si différent, sinon laisser vide
          </div>
          <input
            className="inp" inputMode="tel"
            value={a.ligne2}
            onChange={e => set({ ligne2: e.target.value })}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 12 }}
          />

          <div style={{ marginTop: 18 }} />  {/* Aération */}

          {stepTitle(9, 'Date et heure du rendez-vous')}
          {/* FUSEAU = MONTRÉAL, TOUJOURS (Karim 2026-10-15) — même si la
              campagne indique autre chose. Rappel permanent : vérifier le
              fuseau affiché dans le calendrier du client avant de proposer
              une plage (il peut être différent). */}
          <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8, padding: '8px 10px', marginBottom: 8, lineHeight: 1.5 }}>
            🕐 FUSEAU HORAIRE : MONTRÉAL — heure du Québec, TOUJOURS. L'heure saisie est celle du prospect. Vérifiez toujours le fuseau affiché dans le calendrier du client avant de proposer une plage.
          </div>
          <input
            type="date" className="inp" value={a.date_rdv}
            min={new Date().toISOString().slice(0, 10)}
            onChange={e => set({ date_rdv: e.target.value })}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 8 }}
          />
          <input
            type="time" className="inp" value={a.heure_rdv}
            onChange={e => set({ heure_rdv: e.target.value })}
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 8 }}
          />

          {/* Statut DNCL — TOUJOURS visible dès que date+heure sont saisies.
              L'auto-check reste LA référence (Karim) ; le manuel est le fallback
              si l'auto est indisponible ou contesté par le client. */}
          {a.date_rdv && a.heure_rdv && (
            <div style={{ marginBottom: 12, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {dncl === 'checking' && <span style={{ color: 'var(--text2)', fontWeight: 700 }}>Vérification DNCL automatique en cours…</span>}
              {dncl === 'ok' && <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓ DNCL vérifié automatiquement — numéro non listé</span>}
              {dncl === 'blocked' && <span style={{ color: 'var(--danger)', fontWeight: 700 }}>DNCL : numéro sur la liste d'exclusion — RDV bloqué</span>}
              {dncl == null && <span style={{ color: 'var(--text2)', fontWeight: 700 }}>Vérification automatique indisponible — vérifiez manuellement</span>}
              <button
                type="button" className="btn btn-ghost btn-sm"
                style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}
                onClick={() => setDnclPopup({ checking: false, result: null })}
                title="Ouvre la vérification manuelle du numéro (popup)"
                >Vérification manuelle</button>
            </div>
          )}

          {/* ── Popup : vérification manuelle DNCL (fallback) ───────────────── */}
          {dnclPopup && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }} onClick={e => e.target === e.currentTarget && setDnclPopup(null)}>
              <div style={{
                background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                borderRadius: 12, padding: 20, width: 400, maxWidth: '95vw', boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
              }} onClick={e => e.stopPropagation()}>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>Vérification manuelle DNCL</div>
                <div style={{ fontSize: 11.5, color: 'var(--text2)', marginBottom: 12 }}>
                  Le résultat apparaît ici — à confirmer dans la section « Statut DNCL » ci-dessous.
                </div>
                <div style={{
                  padding: '10px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)',
                  borderRadius: 8, fontSize: 15, fontWeight: 800, fontFamily: 'ui-monospace, monospace', marginBottom: 12,
                }}>
                  {lead?.phone || '—'}
                </div>
                <button type="button" className="btn"
                  onClick={async () => {
                    setDnclPopup({ checking: true, result: null });
                    try {
                      const r = await rdvAPI.dnclCheck(lead?.phone);
                      setDnclPopup({ checking: false, result: r });
                    } catch {
                      setDnclPopup({ checking: false, result: { listed: null } });
                    }
                  }}
                  disabled={dnclPopup.checking}
                  style={{
                    width: '100%', padding: '10px 12px', fontWeight: 800,
                    border: '1px solid var(--border)', background: 'transparent',
                  }}>
                  {dnclPopup.checking ? 'Vérification en cours…' : 'Vérifier maintenant'}
                </button>
                {dnclPopup.result && (
                  <div style={{
                    marginTop: 12, padding: '10px 12px', borderRadius: 8, fontSize: 12.5, fontWeight: 700,
                    background: dnclPopup.result.listed === false ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                    border: `1px solid ${dnclPopup.result.listed === false ? 'var(--green)' : 'var(--danger)'}`,
                    color: dnclPopup.result.listed === false ? 'var(--green)' : 'var(--danger)',
                  }}>
                    {dnclPopup.result.listed === false
                      ? '✓ Résultat du serveur : numéro NON listé sur la liste d\'exclusion.'
                      : dnclPopup.result.listed === true
                        ? `🚫 Résultat du serveur : numéro LISTÉ${dnclPopup.result.reason ? ` — motif : ${dnclPopup.result.reason}` : ''}.`
                        : '⚠ Serveur injoignable — statut à confirmer par le superviseur.'}
                  </div>
                )}
                <button type="button" className="btn btn-ghost" style={{ width: '100%', marginTop: 10, fontSize: 12 }}
                  onClick={() => setDnclPopup(null)}>Fermer</button>
              </div>
            </div>
          )}

          <div style={{ marginTop: 18 }} />  {/* Aération */}

          {stepTitle(10, 'Récapitulatif et confirmation')}
          {pitchBox(<>Je récapitule, votre consultation gratuite se tiendra <strong>{a.mode_rdv === 'virtuel' ? 'en virtuel via Zoom/Teams' : a.mode_rdv === 'telephonique' ? 'par téléphone' : '—'}</strong> le <strong>{a.date_rdv || '—'}</strong> à <strong>{a.heure_rdv || '—'}</strong> (heure du Québec){advisor ? <> avec <strong>{advisor.name}</strong></> : null}. Il va réserver cette plage horaire pour votre consultation gratuite. Je peux compter sur votre présence ?</>)}
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>Commentaires (optionnel)</div>
          <input
            className="inp" value={a.commentaires}
            onChange={e => set({ commentaires: e.target.value })}
            placeholder="Notes pour le suivi…"
            style={{ width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 12 }}
          />
          {/* ── Confirmation OBLIGATOIRE du statut DNCL (Karim, 2026-10-12) ──
              L'auto-check reste la référence. S'il est OK, la case est cochée
              automatiquement. Sinon (indisponible/bloqué/contestation), l'agent
              DOIT sélectionner lui-même le statut du numéro pour débloquer
              (ou bloquer) la prise de rendez-vous. */}
          {a.date_rdv && a.heure_rdv && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text2)', marginBottom: 4 }}>
                Statut DNCL — à confirmer avant de réserver
              </div>
              {dncl === 'blocked' && (
                <div style={{ color: 'var(--danger)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                  Vérification automatique : numéro SUR LA LISTE D'EXCLUSION.
                </div>
              )}
              {dncl == null && (
                <div style={{ color: 'var(--text2)', fontSize: 12, fontWeight: 700, marginBottom: 4 }}>
                  Vérification automatique indisponible — statut à confirmer manuellement.
                </div>
              )}
              {pickPoint('Numéro NON listé (autorise le RDV)', dnclManualOk, () => {
                // Serveur = LISTÉ : le clic ne fait rien — seul un admin peut
                // retirer le numéro de la liste d'exclusion.
                if (dncl === 'blocked') return;
                setDnclManual(dncl === 'ok' ? 'auto-ok' : 'manual-ok');
              })}
              {pickPoint('Numéro LISTÉ (bloque le RDV)', dnclManual === 'listed', () => setDnclManual('listed'))}
              {dnclManual === 'listed' && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--danger)', fontWeight: 700 }}>
                  Numéro sur la liste d'exclusion — la prise de rendez-vous reste bloquée.
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bigBtn('RDV confirmé', () => { if (canBookRdv) finish('rdv'); }, {
              style: { opacity: canBookRdv ? 1 : 0.45 },
            })}
            {bigBtn('Client annule / pas intéressé', () => finish('pas_interesse'))}
          </div>
        </>
      )}
    </>
  );

  // ── onglet Résumé ──────────────────────────────────────────────────────────
  const renderSummary = () => (
    <div>
      {!outcome ? (
        <div style={{ color: 'var(--text2)', fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
          Terminez le script d'appel pour générer le résumé.
        </div>
      ) : (
        <>
          {saveState === 'saving' && <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 8 }}>Enregistrement sur la fiche client…</div>}
          {saveState === 'saved' && <div style={{ fontSize: 11, color: 'var(--green)', marginBottom: 8 }}>Enregistré sur la fiche client — OK</div>}
          {saveState === 'error' && (
            <div style={{
              padding: '10px 12px', marginBottom: 10, borderRadius: 8,
              background: 'rgba(239,68,68,0.12)', border: '1px solid var(--red)',
            }}>
              <div style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--red)', marginBottom: 6 }}>
                ⚠ NON ENREGISTRÉ — le rendez-vous n'est PAS encore dans le système.
              </div>
              {saveError && saveError !== 'APPT_REQUIRES_RDV' && (
                <div style={{ fontSize: 12, color: 'var(--red)', fontWeight: 700, marginBottom: 6 }}>{saveError}</div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={() => finish(outcome)}
                  style={{ flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 800,
                    border: '1px solid var(--red)', background: 'transparent', color: 'var(--red)' }}>
                  ↻ Réessayer
                </button>
                <button className="btn btn-ghost" onClick={() => { setOutcome(null); setSaveState(null); setSaveError(null); setTab('flow'); }}
                  style={{ flex: 1, padding: '8px 10px', fontSize: 12, fontWeight: 700 }}>
                  ↺ Reprendre le script
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 6, lineHeight: 1.5 }}>
                Vos réponses sont conservées. Vous pouvez aussi choisir un autre résultat
                (ex. RAPPEL) dans la disposition et noter les détails en commentaire.
              </div>
            </div>
          )}
          <div style={{
            fontSize: 11.5, lineHeight: 1.7, color: 'var(--text)',
            fontFamily: 'ui-monospace, monospace', whiteSpace: 'pre-wrap',
            padding: '10px 12px', background: 'rgba(255,255,255,0.03)',
            border: '1px solid var(--border)', borderRadius: 8, userSelect: 'text',
          }}>
            {summaryText}
          </div>
          <button className="btn" onClick={copySummary}
            style={{ width: '100%', marginTop: 10, padding: '10px 12px', fontWeight: 700,
              border: '1px solid var(--border)', background: 'transparent' }}>
            {copied ? 'Copié dans le presse-papiers' : 'Copier le résumé'}
          </button>
          <button className="btn" onClick={undoConfirm}
            style={{ width: '100%', marginTop: 8, padding: '8px 12px', fontWeight: 700, fontSize: 12,
              border: '1px solid var(--border)', background: 'transparent' }}>
            ↺ Erreur de validation — annuler et reprendre
          </button>
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text2)', marginBottom: 8 }}>
              Calendriers & suivi des clients
            </div>
            {advisors.filter(x => x.calendar_url || x.spreadsheet_url).length === 0 ? (
              <div style={{ fontSize: 11.5, color: 'var(--text2)', fontStyle: 'italic' }}>
                Aucun lien configuré — ajoutez-les dans le CRM Admin (page Clients).
              </div>
            ) : advisors.filter(x => x.calendar_url || x.spreadsheet_url).map(adv => (
              <div key={adv.id} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                {adv.calendar_url && (
                  <a className="btn" href={adv.calendar_url} target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: '7px 10px', fontSize: 12, textAlign: 'center',
                      border: '1px solid var(--border)', background: 'rgba(255,255,255,0.04)' }}>
                    Calendrier : {adv.name}
                  </a>
                )}
                {adv.spreadsheet_url && (
                  <a className="btn" href={adv.spreadsheet_url} target="_blank" rel="noreferrer"
                    style={{ flex: 1, padding: '7px 10px', fontSize: 12, textAlign: 'center',
                      border: '1px solid var(--border)', background: 'rgba(255,255,255,0.04)' }}>
                    Suivi : {adv.name}
                  </a>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );

  // ── onglet Déroulé (l'ancien TalkTrack, texte brut de campagne) ────────────
  const renderTalk = () => (
    campaign?.script ? (
      <div style={{ fontSize: 13, lineHeight: 1.8, color: 'var(--text)' }}>
        {campaign.script.split('\n').map((line, i) => {
          if (line.startsWith('Agent:') || line.startsWith('AGENT:'))
            return <div key={i} style={{ marginBottom: 10, fontSize: 13.5, fontWeight: 700 }}>
              <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text2)', marginRight: 6 }}>Agent</span>
              {line.replace(/^(Agent|AGENT):/, '').trim()}
            </div>;
          if (line.startsWith('Customer:') || line.startsWith('CUSTOMER:'))
            return <div key={i} style={{ marginBottom: 10, fontSize: 13.5 }}>
              <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text2)', marginRight: 6 }}>Client</span>
              {line.replace(/^(Customer|CUSTOMER):/, '').trim()}
            </div>;
          if (!line.trim()) return <div key={i} style={{ height: 6 }} />;
          return <div key={i} style={{ marginBottom: 4 }}>{line}</div>;
        })}
      </div>
    ) : (
      <div style={{ color: 'var(--text2)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>Aucun script configuré pour cette campagne.</div>
        </div>
    )
  );

  // ── onglet Objections ───────────────────────────────────────────────────────
  const renderObjections = () => (
    <div>
      {OBJECTIONS.map((o, i) => (
        <details key={i} style={{ marginBottom: 8, border: '1px solid var(--border)', borderRadius: 8, background: 'rgba(255,255,255,0.02)' }}>
          <summary style={{ padding: '9px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', color: 'var(--text)' }}>
            {o.q}
          </summary>
          <div style={{ padding: '0 12px 10px', fontSize: 12.5, lineHeight: 1.7, fontWeight: 700, color: 'var(--text)' }}>
            {o.a}
          </div>
        </details>
      ))}
    </div>
  );

  const tabs = [
    ['flow', 'Appel'],
    ['talk', 'Déroulé'],
    ['objections', 'Objections'],
    ['summary', 'Résumé'],
  ];

  return (
    <div className="panel" style={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div className="panel-hdr panel-hdr-green">
        <span>Script d'appel</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {campaign?.name && (
            <span style={{
              fontSize: 10,
              color: 'rgba(255,255,255,0.8)',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 999,
              background: 'rgba(124,58,237,0.25)',
              border: '1px solid rgba(124,58,237,0.4)',
              marginRight: 8
            }}>
              🏷️ {campaign.name}
            </span>
          )}
          {outcome && <span style={{
            fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
            background: 'rgba(255,255,255,0.12)',
          }}>
            {outcome === 'rdv' ? 'RDV confirmé' : outcome === 'hors_cible' ? 'HORS CIBLE' : 'NI'}
          </span>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 2, padding: '6px 8px', borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{
              flex: 1, padding: '6px 4px', fontSize: 10.5, fontWeight: 800,
              borderRadius: 6, border: 'none', cursor: 'pointer',
              background: tab === key ? 'rgba(255,255,255,0.07)' : 'transparent',
              color: tab === key ? 'var(--text)' : 'var(--text2)',
              borderBottom: tab === key ? '2px solid var(--text)' : '2px solid transparent',
            }}>
            {label}
          </button>
        ))}
      </div>

      <div className="panel-body" style={{
        padding: '12px 14px',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
      }}>
        {tab === 'flow' && renderFlow()}
        {calendarAdvisor && (
          <CalendarPopup
            advisor={calendarAdvisor}
            onClose={() => setCalendarAdvisor(null)}
            isOpen
            calendarText={calendarText}
          />
        )}
        {tab === 'talk' && renderTalk()}
        {tab === 'objections' && renderObjections()}
        {tab === 'summary' && renderSummary()}
      </div>
    </div>
  );
}
